import React, { useState } from "react";
import NavBar from "../component/NavBar";
import Footer from "../component/Footer";
import Phone from "../component/Phone";
import { faq } from "../assets";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faQuestionCircle,
  faClock,
  faShieldHalved,
  faCommentDots,
  faCalendarCheck,
  faFileInvoiceDollar,
  faUserCheck,
} from "@fortawesome/free-solid-svg-icons";

const faqData = [
  {
    icon: faClock,
    question: "How soon will someone contact me?",
    answer:
      "We understand that reaching out for help is an important step. Our team is committed to responding quickly to new inquiries. You can expect to hear from us within 24 hours of your initial contact. We prioritize getting back to you promptly so you can begin your journey to wellness without delay.",
  },
  {
    icon: faShieldHalved,
    question: "Do you work with insurance? What if I don't have insurance?",
    answer:
      "Yes, Blissful Days accepts various insurance plans including Aetna, Cigna, CareFirst, Optum, and United Healthcare, as well as Medicare and Medicaid. We recommend contacting us to verify your specific coverage. If you don't have insurance, we offer affordable self-pay rates and flexible payment options to ensure our services remain accessible to everyone who needs them.",
  },
  {
    icon: faCalendarCheck,
    question: "How do I schedule an appointment?",
    answer:
      "Scheduling an appointment is easy. You can contact us through our contact form, call us directly at (240) 610-3766, or email us at info@blissfuldayspsych.com. Our team will work with you to find a convenient time for your telehealth appointment that fits your schedule.",
  },
  {
    icon: faUserCheck,
    question: "What can I expect during my first appointment?",
    answer:
      "Your first appointment is an opportunity for us to get to know you and understand your needs. We'll discuss your mental health history, current concerns, and treatment goals. This comprehensive evaluation typically lasts 45-60 minutes and helps us create a personalized treatment plan tailored to your specific situation.",
  },
  {
    icon: faFileInvoiceDollar,
    question: "What are your payment and billing practices?",
    answer:
      "We accept most major insurance plans and bill them directly on your behalf. For self-pay clients, payment is due at the time of service. We accept credit cards, debit cards, and other electronic payment methods. Our administrative team is always available to discuss payment options and answer any billing questions you may have.",
  },
  {
    icon: faCommentDots,
    question: "What if I have more questions?",
    answer:
      "We're here to help! Feel free to reach out to our dedicated team at Blissful Days Psychiatric Services for any additional questions. You can contact us by phone, email, or through our contact form. We're committed to providing support and addressing your inquiries promptly to ensure you feel comfortable and informed throughout your care journey.",
  },
];

const FAQItem = ({ faq, isOpen, onClick }) => {
  return (
    <div className="bg-white rounded-xl shadow-soft hover:shadow-medium transition-all duration-300 overflow-hidden">
      <button
        onClick={onClick}
        className="w-full flex items-start gap-4 p-6 text-left hover:bg-neutral-50 transition-colors"
      >
        <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
          <FontAwesomeIcon icon={faq.icon} className="text-primary-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-heading-sm text-neutral-900 mb-1 pr-8">
            {faq.question}
          </h3>
        </div>
        <div className="flex-shrink-0 mt-1">
          <FontAwesomeIcon
            icon={isOpen ? faChevronUp : faChevronDown}
            className={`text-primary-500 transition-transform duration-300 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? "max-h-96" : "max-h-0"
        }`}
      >
        <div className="px-6 pb-6 pl-20">
          <p className="text-body-md text-neutral-700 leading-relaxed">
            {faq.answer}
          </p>
        </div>
      </div>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="bg-white">
      <NavBar />
      <Phone />

      <div className="relative h-[400px] md:h-[500px] lg:h-[600px]">
        <img className="w-full h-full object-cover" src={faq} alt="FAQ" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <div className="text-center animate-fade-in">
            <div className="w-16 h-16 bg-primary-500/90 rounded-full flex items-center justify-center mx-auto mb-6">
              <FontAwesomeIcon
                icon={faQuestionCircle}
                className="text-3xl text-white"
              />
            </div>
            <h1 className="text-display-sm md:text-display-md lg:text-display-lg text-white font-bold mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-body-lg md:text-heading-sm text-white/90 max-w-2xl mx-auto">
              Find answers to common questions about our services
            </p>
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-4">
              How Can We Help You?
            </h2>
            <p className="text-body-lg text-neutral-600">
              Click on any question below to view the answer
            </p>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <FAQItem
                key={index}
                faq={faq}
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="py-16 md:py-20 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 md:p-12">
            <h2 className="text-heading-xl md:text-display-sm text-neutral-900 mb-4">
              Still Have Questions?
            </h2>
            <p className="text-body-lg text-neutral-700 mb-8 max-w-2xl mx-auto">
              Can't find the answer you're looking for? Our friendly team is here to help. Reach out to us and we'll get back to you as soon as possible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact">
                <button className="px-8 py-4 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5">
                  Contact Us
                </button>
              </a>
              <a href="tel:2406103766">
                <button className="px-8 py-4 bg-white border-2 border-primary-500 text-primary-600 font-semibold rounded-lg hover:bg-primary-50 transition-all duration-300">
                  Call (240) 610-3766
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQ;
